import type { JSX } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { ConfigProvider } from "antd";
import type { Locale } from "antd/es/locale";
import { useTranslation } from "react-i18next";
import { getAntLocale } from "./antd-locales";

interface LocaleContextValue {
  language: string;
  antLocale: Locale;
  changeLanguage: (lang: string) => Promise<void>;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used within LocaleProvider");
  return ctx;
}

export default function LocaleProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const { i18n } = useTranslation();
  const [language, setLanguage] = useState<string>(i18n.language || "en");
  const [antLocale, setAntLocale] = useState<Locale>(() =>
    getAntLocale(i18n.language || "en")
  );

  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      setLanguage(lng);
      setAntLocale(getAntLocale(lng));
    };
    i18n.on("languageChanged", handleLanguageChanged);
    return () => {
      i18n.off("languageChanged", handleLanguageChanged);
    };
  }, [i18n]);

  const changeLanguage = useCallback(
    (lang: string): Promise<void> => i18n.changeLanguage(lang),
    [i18n]
  );

  return (
    <LocaleContext.Provider value={{ language, antLocale, changeLanguage }}>
      <ConfigProvider locale={antLocale}>{children}</ConfigProvider>
    </LocaleContext.Provider>
  );
}
